import React, { useId, useMemo, useState } from 'react';

import { EditorialProgressRing } from '@/components/reach/editorial/EditorialProgressRing';
import {
  EDITORIAL_WORKFLOW_STATUS_IDS,
  type EditorialWorkflowStatusId,
} from '@/components/reach/editorial/editorial-status-ids';
import {
  HIDE_NARROW,
  Leader,
  NUMERIC_CELL,
  SHEET_ROW,
  SheetCaret,
} from '@/components/ui/grouped-sheet';
import { StatusBadge, type StatusTone } from '@/components/ui/status-badge';
import type { ArticlePublicationSummary } from '@/hooks/reach/useArticleHandoff';
import type { EditorialArticleScore } from '@/hooks/reach/useEditorialArticleTrail';
import type { ArticleApprovalState } from '@/lib/content-doctrine/article-progress';
import { PUBLICATION_STATUS_WORD } from '@/lib/content-doctrine/article-publication-stage';
import { UNFILED_BRANCH_ID } from '@/lib/reach/editorial-chain';
import type { EditorialPillar, EditorialTopic } from '@/lib/reach/editorial-chain';
import { ITEM_META, MICRO_LABEL, TABLE_HEADER_SURFACE } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import {
  type ArticleReviewJob,
  articleStageStatusLabel,
  isArticleJobActive,
} from '@/services/data/ArticleJobService';

/**
 * ONE PILLAR'S PROGRAMME, AS A GROUPED SHEET.
 *
 * 🔴 THIS REPLACES A NODE-AND-EDGE CANVAS, AND THE CANVAS WAS THE PROBLEM.
 *
 * `EditorialChainFlow` drew this same data through `ChainCanvas`: a Pillar
 * column, a Branch column and a Topic column joined by curved leader lines.
 * Measured on the real ITC Tax workspace, the Pillar column held ONE node above
 * roughly two thousand pixels of empty white, six edges ran down past that
 * emptiness to reach branches, and every topic's status printed as identical
 * grey text. Founder, 2026-09-08: _"that white pillar chart with all the leader
 * lines … looks really terrible."_
 *
 * A flow diagram earns its edges when the columns are STAGES and the question
 * is "where does this stop". That was true when the chain had five columns —
 * Pillar · Branch · Topic · Client review · Article. It stopped being true when
 * the chain was cut to three, all of them levels of ONE hierarchy: a hierarchy
 * drawn as a flowchart is an indented list that has paid for a canvas, and it
 * pays in the one axis that does not scroll comfortably.
 *
 * So the stage question moves where it belongs — into a status column you can
 * read down — and the hierarchy renders in the grammar the client's own
 * Strategy pane already uses (`DeskStrategy`). Both import `SHEET_ROW`,
 * `Leader`, `NUMERIC_CELL` and `HIDE_NARROW` from `@/components/ui/grouped-sheet`
 * so the operator's view and the client's view cannot fork.
 *
 * 🔴 STATUS WORDS AND TONES COME FROM `EditorialStatus`, NOT FROM A SECOND
 * DERIVATION. `EditorialTopicTable` renders the same topics one toggle away.
 * Two renderings of one row that disagree about whether it is "Written" is the
 * defect this whole surface keeps producing, so the derivation lives in
 * `editorialTopicStatus` below and BOTH call it.
 */

export { EDITORIAL_WORKFLOW_STATUS_IDS, type EditorialWorkflowStatusId };

export interface EditorialTopicStatus {
  id: EditorialWorkflowStatusId;
  label: string;
  tone: StatusTone;
}

/**
 * The single derivation of a topic's status.
 *
 * Status is DERIVED, never stored. There is already a live status on the row
 * (`project_tasks.status`, which the client's own decision writes) and a real
 * artifact pointer (`artifact_kind`); a third stored status would be a fourth
 * place for one fact to disagree with itself.
 *
 * Order matters, most-advanced-fact-wins throughout: an article that EXISTS
 * outranks whatever the review column says ("Written" is a fact about the
 * artifact, "Approved" a fact about a decision, and a written+approved
 * article is still, to an operator scanning for what is left to do,
 * written); a recorded HANDOFF outranks the mechanical readiness score AND
 * the latest job's outcome. A `partial` or `failed` job never replaces the
 * saved article, so a handoff recorded against it stays true; on the ITC
 * programme 5 of 6 handed-off articles read "Editorial review" instead of
 * "Publish scheduled" while the job outranked the publication. Only a job
 * that is still running outranks a handoff.
 *
 * `score` and `publication` are optional so Map-view callers that have not
 * fetched them yet keep the coarser (but still correct) fallback labels
 * rather than being forced to thread two more bulk queries through first.
 */
export function editorialTopicStatus(
  topic: EditorialTopic,
  job?: ArticleReviewJob,
  score?: Pick<EditorialArticleScore, 'reviewState'> | null,
  publication?: ArticlePublicationSummary | null,
): EditorialTopicStatus {
  if (isArticleJobActive(job)) {
    return { id: 'in-progress', label: articleStageStatusLabel(job!), tone: 'warning' };
  }
  // Short on purpose: this renders in a 128px table column (`w-32`) as well
  // as the side panel. "Draft saved · needs attention" fit the panel but
  // wrapped to two lines in the table — the draft's existence is already
  // visible from the row being openable, so the state word alone is enough.
  if (topic.hasArticle && publication) {
    const tone = publication.state === 'published' ? 'success' : 'info';
    const id = (
      {
        published: 'published',
        scheduled: 'scheduled',
        images: 'images',
        seo_review: 'seo-review',
        handed_off: 'handed-off',
      } as const
    )[publication.state];
    return { id, label: PUBLICATION_STATUS_WORD[publication.state], tone };
  }
  // A claim check writes its own record and never the article, so once it has
  // settled its outcome says nothing about the draft's editorial state. The
  // dock still shows it; the status ladder does not read it as "Last run failed".
  // An address pass that changed nothing leaves the article as it was, so its `partial` is not "Editorial review" either.
  const settledJob =
    job?.operation === 'factcheck' || job?.operation === 'address' ? undefined : job;
  if (topic.hasArticle && settledJob?.status === 'partial') {
    return { id: 'editorial-review', label: 'Editorial review', tone: 'warning' };
  }
  if (topic.hasArticle && settledJob?.status === 'failed') {
    return { id: 'run-failed', label: 'Last run failed', tone: 'warning' };
  }
  if (topic.hasArticle && score) {
    if (score.reviewState === 'ready') {
      return { id: 'ready', label: 'Ready to hand off', tone: 'success' };
    }
    if (score.reviewState === 'needs-review') {
      return { id: 'needs-review', label: 'Needs review', tone: 'warning' };
    }
    if (score.reviewState === 'claims-pending') {
      return { id: 'claims-pending', label: 'Check claims', tone: 'warning' };
    }
    return { id: 'review-limited', label: 'Review limits', tone: 'neutral' };
  }
  if (topic.hasArticle) return { id: 'written', label: 'Draft written', tone: 'success' };
  if (topic.status === 'approved') return { id: 'approved', label: 'Topic approved', tone: 'info' };
  if (topic.status === 'review')
    return { id: 'needs-review', label: 'Needs review', tone: 'warning' };
  return { id: 'proposed', label: 'Proposed', tone: 'neutral' };
}

/**
 * The one rendering of a topic's status. The Map's rows and the Table's Status column both mount it
 * with the output of `editorialTopicStatus`, so the pill's word, tone and fill cannot differ by view.
 * `fill="outline"` matches `DeskItemStatusHeader`'s undecided-item pill, the one the founder compared
 * the Table against (2026-09-09).
 */
export function EditorialStatusBadge({
  status,
}: {
  status: EditorialTopicStatus;
}): React.JSX.Element {
  return (
    <StatusBadge tone={status.tone} fill="outline">
      {status.label}
    </StatusBadge>
  );
}

interface BranchGroup {
  id: string;
  name: string;
  topics: EditorialTopic[];
  written: number;
  /**
   * This branch's share of the PILLAR's topics — not of the whole programme.
   * The pillar cards above already carry programme share; repeating it here
   * would be the same number twice, while "how concentrated is this pillar"
   * is the question the sheet is uniquely placed to answer. On the real ITC
   * data one branch holds 4 of 15 and two hold 1 each, which is the sort of
   * lopsidedness a count column alone makes you compute in your head.
   */
  sharePct: number;
}

function pct(part: number, whole: number): number {
  return whole === 0 ? 0 : Math.round((part / whole) * 100);
}

function groupByBranch(pillar: EditorialPillar, topics: EditorialTopic[]): BranchGroup[] {
  const byBranch = new Map<string, EditorialTopic[]>();
  for (const branch of pillar.branches) byBranch.set(branch.id, []);

  const unfiled: EditorialTopic[] = [];
  for (const topic of topics) {
    if (topic.branchId && byBranch.has(topic.branchId)) byBranch.get(topic.branchId)?.push(topic);
    else unfiled.push(topic);
  }

  const groups: BranchGroup[] = pillar.branches.map((branch) => {
    const branchTopics = byBranch.get(branch.id) ?? [];
    return {
      id: branch.id,
      name: branch.name,
      topics: branchTopics,
      written: branchTopics.filter((t) => t.hasArticle).length,
      sharePct: pct(branchTopics.length, topics.length),
    };
  });

  /*
   * A branch with ZERO topics is still drawn. A sub-pillar the programme
   * agreed to cover and has written nothing for is the most actionable row on
   * this page — dropping it would hide exactly the gap an operator opens this
   * view to find. It carries no caret, because there is nothing to open.
   */
  if (unfiled.length > 0) {
    groups.push({
      id: UNFILED_BRANCH_ID,
      name: 'Not filed to a branch',
      topics: unfiled,
      written: unfiled.filter((t) => t.hasArticle).length,
      sharePct: pct(unfiled.length, topics.length),
    });
  }
  return groups;
}

function TopicRow({
  topic,
  onOpenTopic,
  job,
  score,
  publication,
  approval,
}: {
  topic: EditorialTopic;
  job?: ArticleReviewJob;
  score?: EditorialArticleScore | null;
  publication?: ArticlePublicationSummary | null;
  approval?: ArticleApprovalState | null;
  onOpenTopic?: (topic: EditorialTopic) => void;
}): React.JSX.Element {
  const Element = onOpenTopic ? 'button' : 'div';
  const [rowFocused, setRowFocused] = useState(false);
  const status = editorialTopicStatus(topic, job, score, publication);
  return (
    <Element
      type={onOpenTopic ? 'button' : undefined}
      onClick={onOpenTopic ? () => onOpenTopic(topic) : undefined}
      onFocus={(event) => setRowFocused(event.currentTarget.matches(':focus-visible'))}
      onBlur={() => setRowFocused(false)}
      aria-label={onOpenTopic ? `Open the topic "${topic.topic}"` : undefined}
      aria-describedby={topic.hasArticle ? `progress-${topic.id}` : undefined}
      className={cn(
        SHEET_ROW,
        'w-full py-1.5 pl-[22px] pr-3 text-left',
        onOpenTopic && 'hover:bg-muted/30 focus-visible:outline focus-visible:outline-ring',
      )}
    >
      {/*
       * The ring leads the row, in the same 20px column the branch caret uses,
       * so it never sits under a numeric header (it read as a "Drafts" figure
       * when it trailed the status badge).
       */}
      <span className="flex items-center justify-center">
        {topic.hasArticle ? (
          <EditorialProgressRing
            hasArticle
            statusId={status.id}
            describedById={`progress-${topic.id}`}
            score={score}
            approval={approval}
            publication={publication}
            focusable={!onOpenTopic}
            forceOpen={rowFocused}
          />
        ) : (
          <span aria-hidden="true" className="size-5 shrink-0" />
        )}
      </span>
      <span className="flex min-w-0 items-baseline max-[720px]:col-span-2 ">
        <span className="line-clamp-2 text-caption text-foreground/90">{topic.topic}</span>

        <span className="flex-1 max-[720px]:hidden">
          <Leader />
        </span>
      </span>
      {/*
       * The status spans the three numeric tracks rather than sitting in one.
       * A badge is wider than a figure, and squeezing it into a 60px numeric
       * column either clips "In progress" or forces the column wider for every
       * count on the sheet. It stays right-aligned so it still reads as a
       * column down the list.
       */}
      <span className="col-span-3 flex items-center justify-end gap-2 max-[720px]:col-span-2 max-[720px]:col-start-2 max-[720px]:justify-start max-[720px]:pt-1">
        <EditorialStatusBadge status={status} />
      </span>
    </Element>
  );
}

function BranchRow({
  group,
  onOpenTopic,
  jobsByTopic,
  scores,
  publications,
  approvals,
}: {
  group: BranchGroup;
  jobsByTopic?: Map<string, ArticleReviewJob>;
  scores?: Map<string, EditorialArticleScore>;
  publications?: Map<string, ArticlePublicationSummary>;
  approvals?: Map<string, ArticleApprovalState>;
  onOpenTopic?: (topic: EditorialTopic) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const hasTopics = group.topics.length > 0;

  const row = (
    <>
      <span className="flex items-center justify-center">
        {hasTopics ? <SheetCaret open={open} /> : <span aria-hidden="true" />}
      </span>
      <span className="flex min-w-0 items-baseline">
        <span className="truncate text-caption text-foreground">{group.name}</span>
        <Leader />
      </span>
      <span className={NUMERIC_CELL}>{group.topics.length}</span>
      <span className={cn(NUMERIC_CELL, HIDE_NARROW)}>{group.written}</span>
      <span className={cn(NUMERIC_CELL, HIDE_NARROW)}>{group.sharePct}%</span>
    </>
  );

  return (
    <>
      {hasTopics ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={panelId}
          className={cn(SHEET_ROW, 'w-full py-1.5 pl-[22px] pr-3 text-left hover:bg-muted/30')}
        >
          {row}
        </button>
      ) : (
        <div className={cn(SHEET_ROW, 'py-1.5 pl-[22px] pr-3')}>{row}</div>
      )}
      {open ? (
        <div id={panelId}>
          {group.topics.map((topic) => (
            <TopicRow
              key={topic.id}
              topic={topic}
              onOpenTopic={onOpenTopic}
              job={jobsByTopic?.get(topic.id)}
              score={scores?.get(topic.id)}
              publication={publications?.get(topic.id)}
              approval={approvals?.get(topic.id)}
            />
          ))}
        </div>
      ) : null}
    </>
  );
}

export interface EditorialPillarSheetProps {
  pillar: EditorialPillar;
  topics: EditorialTopic[];
  className?: string;
  jobsByTopic?: Map<string, ArticleReviewJob>;
  /**
   * Keyed by TOPIC id, not artifact id — unlike `useEditorialArticleScores`'s
   * own map. The Table (`EditorialTopicTable`) reads that query directly
   * because it already has each row's `artifactId`; this sheet's `topics`
   * prop is the narrower `EditorialTopic` without one, so the page re-keys
   * the same query result by topic id before handing it down, and both
   * surfaces end up reading the identical fetch.
   */
  scores?: Map<string, EditorialArticleScore>;
  publications?: Map<string, ArticlePublicationSummary>;
  approvals?: Map<string, ArticleApprovalState>;
  onOpenTopic?: (topic: EditorialTopic) => void;
}

export function EditorialPillarSheet({
  pillar,
  topics,
  className,
  onOpenTopic,
  jobsByTopic,
  scores,
  publications,
  approvals,
}: EditorialPillarSheetProps): React.JSX.Element {
  const groups = useMemo(() => groupByBranch(pillar, topics), [pillar, topics]);
  const written = topics.filter((t) => t.hasArticle).length;

  return (
    <div className={cn('rounded-xl border border-border/60', className)}>
      {/* Same quiet band as every other table head in the app, and as the
          client's Strategy sheet — one treatment, two surfaces. */}
      <div
        className={cn(
          SHEET_ROW,
          TABLE_HEADER_SURFACE,
          'rounded-t-xl border-b border-border/40 py-2 pl-[22px] pr-3',
          MICRO_LABEL,
        )}
      >
        <span aria-hidden="true" />
        <span>Branch</span>
        <span className="text-right">Topics</span>
        <span className={cn('text-right', HIDE_NARROW)}>Drafts</span>
        <span className={cn('text-right', HIDE_NARROW)}>Share</span>
      </div>

      {groups.map((group) => (
        <BranchRow
          key={group.id}
          group={group}
          onOpenTopic={onOpenTopic}
          jobsByTopic={jobsByTopic}
          scores={scores}
          publications={publications}
          approvals={approvals}
        />
      ))}

      <div className={cn(SHEET_ROW, 'border-t border-border/40 py-2 pl-[22px] pr-3', ITEM_META)}>
        <span aria-hidden="true" />
        <span>
          {topics.length} topic{topics.length === 1 ? '' : 's'} across {groups.length} branch
          {groups.length === 1 ? '' : 'es'}
        </span>
        <span className={cn(NUMERIC_CELL, 'font-medium text-foreground')}>{topics.length}</span>
        <span className={cn(NUMERIC_CELL, HIDE_NARROW, 'font-medium text-foreground')}>
          {written}
        </span>
        {/* 100%, always — every topic under this pillar lands in exactly one
            group because `groupByBranch` sweeps the remainder into "Not filed
            to a branch" rather than dropping it. A footer that did NOT total
            100 would mean topics had gone missing, so it is worth printing. */}
        <span className={cn(NUMERIC_CELL, HIDE_NARROW, 'font-medium text-foreground')}>
          {topics.length === 0 ? '—' : '100%'}
        </span>
      </div>
    </div>
  );
}
